import React, { useEffect, useMemo, useState } from 'react';

import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import i18n from 'i18n-react';
import { HashLink as Link } from 'react-router-hash-link';
import { legacyRemCss } from 'helpers/legacyRem';

type LocalizedText = string | Record<string, string>;

interface TermsBlock {
  type: 'paragraph' | 'heading' | 'span';
  text: LocalizedText;
}

interface TermsSchema {
  title?: LocalizedText;
  subTitle?: LocalizedText;
  button?: LocalizedText;
  content?: TermsBlock[];
}

function resolveText(value: LocalizedText | undefined, langFile: string, fallback = 'en'): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[langFile] ?? value[fallback] ?? value[Object.keys(value)[0]] ?? '';
}

async function loadTermsSchema(appId: string): Promise<TermsSchema | null> {
  try {
    if (!appId) return null;

    const url = `https://front.travpromobile.com/${appId}/terms.schema.json`;

    const res = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      console.warn(`Terms schema not found for appId ${appId}`);
      return null;
    }

    const json = (await res.json()) as TermsSchema;
    return json;
  } catch (error) {
    console.error('Failed to load terms schema from S3:', error);
    return null;
  }
}

const TermsAndConditionsView: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const mobile = Boolean(params.get('mobile'));
  const email = params.get('email') ?? '';

  const [terms, setTerms] = useState<TermsSchema | null>(null);
  const [langFile, setLangFile] = useState('en');

  useEffect(() => {
    void (async () => {
      const appId = String(import.meta.env.VITE_APP_ID || '');
      if (!appId) return;

      const lang = (await StorageHandler.load(storageKeys.lang)) as { langFile?: string } | null;
      setLangFile(String(lang?.langFile));

      const schema = await loadTermsSchema(appId);
      setTerms(schema);
    })();
  }, []);

  const fallback = useMemo(
    () => ({
      title: i18n.translate('rewards.TermsAndConditions.title'),
      subTitle: i18n.translate('rewards.TermsAndConditions.subTitle'),
      body1: i18n.translate('rewards.TermsAndConditions.body1'),
      body2: i18n.translate('rewards.TermsAndConditions.body2'),
      body3: i18n.translate('rewards.TermsAndConditions.body3'),
      body4: i18n.translate('rewards.TermsAndConditions.body4'),
      body5: i18n.translate('rewards.TermsAndConditions.body5'),
      button: i18n.translate('rewards.TermsAndConditions.button'),
    }),
    [],
  );

  const title = resolveText(terms?.title, langFile) || fallback.title;
  const buttonText = resolveText(terms?.button, langFile) || fallback.button;

  const backLink = mobile ? `/main/rewards?mobile=true&email=${encodeURIComponent(email)}` : '/main/rewards';

  return (
    <div className="contentContainer BookingForm">
      <div className="header">
        <h3>{title}</h3>
        {terms?.subTitle && <h6>{resolveText(terms.subTitle, langFile)}</h6>}
      </div>

      <div className="bodyRewardsTerms">
        {/* Dynamic schema content */}
        {terms?.content?.length ? (
          terms.content.map((block, idx) => {
            const text = resolveText(block.text, langFile);
            if (!text) return null;

            if (block.type === 'heading') {
              return <span key={idx}>{text}</span>;
            }

            if (block.type === 'span') {
              return (
                <p key={idx}>
                  <span>{text}</span>
                </p>
              );
            }

            // paragraph
            return <p key={idx}>{text}</p>;
          })
        ) : (
          // Fallback to old static i18n layout
          <p>
            {fallback.body1}
            <br />
            <br />
            {fallback.body2}
            <br />
            <br />
            {fallback.body3}
            <br />
            <br />
            <span>{fallback.body4}</span>
            <br />
            {fallback.body5}
            <br />
            <br />
          </p>
        )}
        <br />
        <br />
        <hr />

        <div className="buttonHolder">
          <Link to={backLink}>
            <button className="btn" type="button">
              {buttonText} &gt;
            </button>
          </Link>
        </div>
      </div>

      {mobile && (
        <style>
          {`.contentContainer { margin-top: ${legacyRemCss(5)}; } .header { display: none; } .cookieConsent { display: none; }`}
        </style>
      )}
    </div>
  );
};

export default TermsAndConditionsView;
